import { useT } from '@/i18n/react';
import { useState } from 'react';
import { Icon } from '@/components/Icon';
import { ASSISTANT_TEXT_MAX } from '@/lib/routeAssistant';
import { useAssistantStore, type AssistantSurface } from '@/store/assistantStore';
import './assistantPrompt.css';

/**
 * „Grout” gyors mezője (#79) — egy sor, küldés gombbal.
 *
 * - Rögzítés képernyő (`tracking`), a mozgásforma fölött: ha a kérés egyből
 *   tervvé fordítható, a tervező nyílik előtöltve; ha nem, a chat.
 * - Home (`home`), a bannerek alatt: mindig a chat nyílik (Geri, #79).
 *
 * A beszélgetés a chaté (`assistantStore`) — innen csak indul vagy folytatódik.
 */
export function AssistantPrompt({
  surface,
  title,
  placeholder,
  className,
}: {
  surface: Exclude<AssistantSurface, 'chat'>;
  title?: string;
  placeholder: string;
  className?: string;
}) {
  const t = useT();
  const send = useAssistantStore((state) => state.send);
  const busy = useAssistantStore((state) => state.busy);
  const chatOpen = useAssistantStore((state) => state.open);
  const error = useAssistantStore((state) => state.error);
  const openChat = useAssistantStore((state) => state.openChat);
  const hasConversation = useAssistantStore((state) => state.entries.length > 0 || state.conversationId !== null);
  const [text, setText] = useState('');
  const [mine, setMine] = useState(false);
  const canSend = text.trim().length > 0 && !busy;

  async function submit() {
    if (!canSend) return;
    const value = text;
    setMine(true);
    setText('');
    const ok = await send(value, surface);
    if (!ok) setText((current) => current || value);
  }

  /* A hiba csak annál a mezőnél látszik, ahonnan a kérés ment, és csak csukott chatnél. */
  const showError = mine && !chatOpen && error;

  return (
    <div className={`ap${className ? ` ${className}` : ''}`}>
      {title ? (
        <div className="ap__head">
          <span className="ap__title">{title}</span>
          {hasConversation ? (
            <button type="button" className="ap__open" onClick={() => openChat()}>
              {t.assistantPrompt.conversation}
            </button>
          ) : null}
        </div>
      ) : null}
      <form
        className="ap__form"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <span className="ap__avatar" aria-hidden="true">
          <Icon name="chat" size={16} />
        </span>
        <input
          className="ap__input"
          type="text"
          maxLength={ASSISTANT_TEXT_MAX}
          enterKeyHint="send"
          placeholder={placeholder}
          aria-label={placeholder}
          value={text}
          disabled={busy && mine}
          onChange={(event) => setText(event.target.value)}
        />
        <button type="submit" className="ap__send" aria-label={t.assistantPrompt.send} disabled={!canSend}>
          {busy && mine ? <span className="ap__spinner" aria-hidden="true" /> : <Icon name="send" size={18} />}
        </button>
      </form>
      {showError ? <p className="ap__error">{error}</p> : null}
    </div>
  );
}
